import {Delete, Email} from "@mui/icons-material";
import DownloadIcon from "@mui/icons-material/Download";
import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {Button, IconButton, Tooltip} from "@mui/material";
import Grid from "@mui/material/Grid";
import dayjs from "dayjs";
import {useSnackbar} from "notistack";
import React, {useContext, useState} from "react";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import {genderCode, getGenderCodeValue} from "../../constants/genderCode";
import {resources} from "../../services/ability";
import {AbilityContext} from "../../services/Can";
import {getErrorMessage, MESSAGES, post} from "../../services/Client";
import {formatAddress} from "../../services/helper";
import StartIconButton from "../buttons/StartIconButton";
import RenderBoolean from "../cellRender/RenderBoolean";
import RenderCell from "../cellRender/RenderCell";
import RenderEditableUsername from "../cellRender/RenderEditableUsername";
import RenderUserAddressHistory from "../cellRender/RenderUserAddressHistory";
import SelectUsersByEmailModal from "../modals/SelectUsersByEmailModal";
import SendMessageModal from "../modals/SendMessageModal";
import NTMXGrid, {timestampFormatter, timestampFormatterWithTimezone} from "../NTMXGrid";
import { useGetOrganizations } from "services/ContentManager";

/**
 * Tabella utenti. Senza {@code serverSide} lavora sulle righe ricevute (tabella degli utenti di
 * un'iniziativa); con {@code serverSide} filtri, ordinamento e pagine li fa il backend (tabella
 * Utenti dell'admin) e la prop porta i callback: rowCount, onFilterModelChange, onSortModelChange,
 * onPageChange, onPageSizeChange, loadAllTargets, resolveUsers, exportCsv.
 */
export default function UserTable({
	                                  users = [],
	                                  status,
	                                  saveEdit,
	                                  showSerialNumber,
	                                  showIban,
	                                  customColumns,
	                                  showOrganizationName,
	                                  rightButton,
	                                  allowEditEmail,
	                                  sendEmailValidation,
	                                  deleteUser,
	                                  title,
	                                  showActivationDate,
	                                  selectUsersByEmailVisible = false,
	                                  serverSide
                                  }) {

	const {t} = useTranslation();
	const ability = useContext(AbilityContext);
	const canReadPrivateData = ability.can("read", resources.PRIVATEUSERDATA);
	const canReadUserWorkAddresses = ability.can("read", resources.USER_WORK_ADDRESSES);
	const {enqueueSnackbar} = useSnackbar();
	let [selectedRows, setSelectedRows] = useState([]);
	let [isSendNotificationModalOpen, setIsSendNotificationModalOpen] = useState(false);
	let [targetsToSendMessage, setTargetsToSendMessage] = useState([]);
	let [isSendingMessageToSpecificUsers, setIsSendingMessageToSpecificUsers] = useState(false);
	let {status: organizationStatus, organizations} = useGetOrganizations();

	const isServer = !!serverSide;
	// Chi e' in cancellazione o anonimizzato non si modifica, non si elimina e non riceve mail di verifica.
	const isActive = (row) => !row.deletionStatus || row.deletionStatus === "ACTIVE";
	const toDate = ({value}) => value ? new Date(value) : null;
	const formatDay = ({value}) => value ? dayjs(value).format("DD/MM/YYYY") : "";
	const formatInstant = ({value}) => value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "";
	const notFilterable = isServer ? {filterable: false, sortable: false} : {};

	const sendMessage = (values) => {
		enqueueSnackbar(t('sending...'), {variant: "info"});
		// console.log(selectedRows)
		const uids = getSendMessageModalTarget().map(u => u.uid);
		values.sendTo = uids;

		post(`${MESSAGES}/send`, {body: values})
			.then(() => {
				setTargetsToSendMessage([]);
				setIsSendNotificationModalOpen(false);
				enqueueSnackbar(t('sent'), {variant: "success"});
			})
			.catch(e => enqueueSnackbar(getErrorMessage(e), {variant: "error"}));
	};

	// Un elenco vuoto non deve aprire il modale: getSendMessageModalTarget ripiegherebbe sulle righe visibili.
	const openTargets = (targets) => {
		if (targets.length === 0) {
			enqueueSnackbar(t('noUsersFound'), {variant: "warning"});
			return;
		}
		setTargetsToSendMessage(targets);
		setIsSendNotificationModalOpen(true);
	};

	// Senza selezione, in modalita' server, il messaggio va a tutti gli utenti attivi che rispettano i filtri.
	const openSendModal = () => {
		if (isServer && selectedRows.length === 0) {
			serverSide.loadAllTargets()
				.then(openTargets)
				.catch(e => enqueueSnackbar(getErrorMessage(e), {variant: "error"}));
		} else setIsSendNotificationModalOpen(true);
	};

	const sendMessageToUsersList = (usersList) => {
		if (isServer) {
			serverSide.resolveUsers(usersList)
				.then(openTargets)
				.catch(e => enqueueSnackbar(getErrorMessage(e), {variant: "error"}));
			return;
		}
		setTargetsToSendMessage(users.filter(u => usersList.includes(u.email) || usersList.includes(u.uid)));
		setIsSendNotificationModalOpen(true);
	};

	const columns = [
		{
			headerName: t('userID'),
			field: 'uid',
			width: 280,
		},
		showActivationDate && {
			headerName: t('activationDate'),
			field: 'activationDate',
			width: 280,
			...timestampFormatterWithTimezone
		},
		showActivationDate && {
			headerName: t('activeEnrollment'),
			field: 'endDate',
			width: 180,
			type: "boolean",
			valueGetter: ({value}) => dayjs(value).isAfter(dayjs(new Date())),
			renderCell: (params) => <RenderBoolean params={params}></RenderBoolean>
		},
		{
			headerName: t('email'),
			field: 'email',
			width: 220,
			renderCell: allowEditEmail ? (params) => isActive(params.row) ?
					<RenderCell
						params={params}
						saveEdit={(id, field, value) => saveEdit(params.row.uid, field, value)}
					/>
					: params.value
				: undefined
		},
		{
			headerName: t('phoneNumber'),
			field: 'phoneNumber',
			width: 170,
			//hide: true,
			renderCell: (params) => <RenderCell params={params}
			                                    saveEdit={(id, field, value) => saveEdit(params.row.uid, field, value)}/>
		},
		isServer ? {
			headerName: t('birthDate'),
			field: 'birthDate',
			width: 180,
			type: 'date',
			valueGetter: toDate,
			valueFormatter: formatDay
		} : {
			headerName: t('birthDate'),
			field: 'birthDate_date',
			width: 180,
			//hide: true,
			...timestampFormatter
		},
		{
			headerName: t('username'),
			field: 'username',
			width: 130,
			renderCell: (params) => <RenderEditableUsername params={params} saveEdit={saveEdit} key={params.row.uid}/>
			/*renderCell: (params) => <RenderUserUidRedirect username={params.value} uid={params.row.uid}/>*/
		},
		{
			headerName: t('firstName'),
			field: 'firstName',
			width: 130,
			renderCell: (params) => <RenderCell params={params}
			                                    saveEdit={(id, field, value) => saveEdit(params.row.uid, field, value)}/>
		},
		{
			headerName: t('lastName'),
			field: "lastName",
			width: 130,
			renderCell: (params) => <RenderCell params={params}
			                                    saveEdit={(id, field, value) => saveEdit(params.row.uid, field, value)}/>
		},
		{
			headerName: t('gender'),
			field: "genderCode",
			valueFormatter: (params) => t(getGenderCodeValue(params.value)),
			width: 130,
			...(isServer ? {
				type: 'singleSelect',
				valueOptions: Object.values(genderCode).map(g => ({value: g.id, label: t(g.value)}))
			} : {})
			//hide: true
		},
		{
			headerName: t('fiscalCode'),
			field: 'fiscalCode',
			width: 180,
			//hide: true,
			renderCell: (params) => <RenderCell params={params}
			                                    saveEdit={(id, field, value) => saveEdit(params.row.uid, field, value)}/>
		},
		showSerialNumber && {
			headerName: t('serialNumber'),
			field: 'serialNumber',
			width: 220,
			//hide: true,
			renderCell: (params) => <RenderCell params={params}
			                                    saveEdit={(id, field, value) => saveEdit(params.row.uid, field, value)}/>
		},
		showIban && {
			headerName: t('iban'),
			field: 'iban',
			width: 220,
			//hide: true,
			renderCell: (params) => <RenderCell params={params}
			                                    saveEdit={(id, field, value) => saveEdit(params.row.uid, field, value)}/>
		},
		// Nella tabella admin il cluster non arriva: la colonna resterebbe sempre vuota.
		!isServer && {
			headerName: 'Cluster',
			field: 'customField',
			width: 180,
		},
		canReadPrivateData && {
			headerName: t('homeAddress'),
			field: 'homeAddresses',
			width: 250,
			...notFilterable,
			valueFormatter: ({value}) => value ? value.map(a => `${formatAddress(a)} (${a.latitude}-${a.longitude})`).join('\n') : "",
			renderCell: ({value, colDef}) => value && <RenderUserAddressHistory addresses={value} width={colDef.width}/>
		},
		(canReadPrivateData || canReadUserWorkAddresses) && {
			headerName: t('workAddresses'),
			field: 'workAddresses',
			width: 250,
			...notFilterable,
			//hide: true,
			valueFormatter: ({value}) => value ? value.map(a => `${formatAddress(a)} (${a.latitude}-${a.longitude})`).join('\n') : "",
			renderCell: ({value, colDef}) => value && <RenderUserAddressHistory addresses={value} width={colDef.width}
			                                                                    showOrganizationName={showOrganizationName}/>
		},
		canReadPrivateData && (isServer ? {
			field: "lastLoggedIn",
			headerName: t('lastLoggedIn'),
			width: 200,
			type: 'date',
			valueGetter: toDate,
			valueFormatter: formatInstant
		} : {
			field: "lastLoggedIn",
			headerName: t('lastLoggedIn'),
			width: 280,
			...timestampFormatterWithTimezone,
			//hide: true
		}),
		isServer && {
			headerName: t('status'),
			field: 'deletionStatus',
			width: 160,
			type: 'singleSelect',
			valueOptions: [
				{value: 'ACTIVE', label: t('active')},
				{value: 'PENDING_DELETION', label: t('pendingDeletion')},
				{value: 'ANONYMIZED', label: t('anonymized')}
			]
		},
		isServer && {
			headerName: t('deletedAt'),
			field: 'deletedDate',
			width: 170,
			type: 'date',
			valueGetter: toDate,
			valueFormatter: formatInstant
		},
		isServer && {
			headerName: t('deletionDeadline'),
			field: 'deletionDeadline',
			width: 190,
			type: 'date',
			valueGetter: toDate,
			valueFormatter: formatInstant,
			...notFilterable
		},
		{
			headerName: t('details'),
			field: "details",
			width: 150,
			sortable: false,
			filterable: false,
			disableColumnMenu: true,
			disableExport: true,
			hideable: false,
			renderCell: (params) => <Grid container direction={"row"} justifyContent="center">
				<Grid item><Link to={"/profile/" + params.row.uid}
				                 target="_blank"><IconButton><VisibilityIcon/></IconButton></Link></Grid>
				{
					deleteUser && isActive(params.row) &&
					<Grid item><IconButton onClick={() => deleteUser(params.row)}><Delete/></IconButton></Grid>
				}
				{
					sendEmailValidation && isActive(params.row) &&
					<Grid item>
						<Tooltip title={t('sendEmailValidation')}>
							<IconButton
								onClick={() => sendEmailValidation(params.row.uid)}
							>
								<Email/>
							</IconButton>
						</Tooltip>
					</Grid>
				}
			</Grid>
		}
	].filter(Boolean);

	if(customColumns) columns.push(...customColumns);
	if (!isServer) users = users.slice().sort((a, b) => (b.lastLoggedIn || 0) - (a.lastLoggedIn || 0));

	const getSendMessageModalTarget = () => {
		if(targetsToSendMessage.length !== 0) return targetsToSendMessage;
		if(selectedRows.length === 0) return users;

		let target = users.filter(u => selectedRows.includes(u.activationDate ? u.uid + u.activationDate : u.uid));
		return [...new Map(target.map(t => [t.uid, t])).values()];
	};

	// In modalita' server la selezione vale per la pagina visibile: cambiando pagina o filtri si azzera,
	// cosi' un invio non parte verso righe che l'operatore non vede piu'.
	const serverProps = isServer ? {
		filterMode: "server",
		sortingMode: "server",
		paginationMode: "server",
		rowCount: serverSide.rowCount,
		page: serverSide.page,
		onFilterModelChange: (model) => {
			setSelectedRows([]);
			serverSide.onFilterModelChange(model);
		},
		onSortModelChange: serverSide.onSortModelChange,
		onPageChange: (page) => {
			setSelectedRows([]);
			serverSide.onPageChange(page);
		},
		onPageSizeChange: (size) => {
			setSelectedRows([]);
			serverSide.onPageSizeChange(size);
		}
	} : {};

	return <>
		<NTMXGrid
			checkboxSelection
			columns={columns}
			rows={users || {}}
			title={title ?? t('users')}
			getRowId={(row) => isServer ? row.uid : users && (row.activationDate ? row.uid + row.activationDate : row.uid)}
			loading={status === "loading"}
			onSelectionModelChange={(newSelection) => {
				setSelectedRows(newSelection);
			}}
			selectionModel={selectedRows}
			disableExport={isServer}
			{...serverProps}
			rightButton={
				<Grid container justifyContent={"flex-end"}>
					{
						rightButton
					}
					{isServer &&
						<Button color="primary" startIcon={<DownloadIcon/>} onClick={serverSide.exportCsv}>
							{t('exportCSV')}
						</Button>}
					<Button
						color="primary"
						startIcon={<SendIcon/>}
						disabled={!isServer && selectedRows.length === 0}
						onClick={openSendModal}
					>
						{t('sendNotification')}
					</Button>
					{selectUsersByEmailVisible &&
						<StartIconButton onClick={() => setIsSendingMessageToSpecificUsers(true)}
						                 title={t('sendMessageToUsers')} startIcon={<SendIcon/>}/>}
				</Grid>
			}
			initialState={{pinnedColumns: {right: ['details']}}}
		/>

		{isSendNotificationModalOpen &&
			<SendMessageModal
				targets={getSendMessageModalTarget()}
				open={!!isSendNotificationModalOpen}
				organizations={organizations}
				onClose={() => {
					setIsSendNotificationModalOpen(false);
					setTargetsToSendMessage([]);
				}}
				onSave={sendMessage}/>
		}

		{selectUsersByEmailVisible &&
			<SelectUsersByEmailModal
				open={!!isSendingMessageToSpecificUsers}
				onClose={() => setIsSendingMessageToSpecificUsers(false)}
				onSave={sendMessageToUsersList}/>
		}

	</>;

}
